import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Form, Field } from 'react-final-form'
import cn from 'classnames'
import useMutation from '../../hooks/useMutation'
import updateResourceType from '../../graphql/mutations/updateOnlineScheduleResourceType'
import styles from './index.module.scss'
import { TextareaInput } from '../TextareaInput'
import { SelectInput } from '../SelectInput'
import { CheckboxInput } from '../CheckboxInput'
import notify from '../../utilities/notify'
import { captureErrorAndNotify } from '../../utilities/errorHandlers'

export default function OnlineScheduleProductDetails ({
  assetDetails,
  startExpanded = false,
  allowExpand = true,
  haulerCountry = 'US',
  haulerId,
  pricingDisabled = false,
  initialValues,
  refetchAssetTypes
}) {
  const [expanded, setExpanded] = useState(startExpanded)
  const isEnabled = assetDetails.status.toLowerCase() === 'enabled'

  const { mutateAsync: updateResourceTypeMutation, isLoading: isUpdating } = useMutation(updateResourceType, {
    async onSuccess ({ resourceType }) {
      refetchAssetTypes()
      notify('success', `Asset type ${resourceType.shortCode} updated for Online Schedule`)
    },
    onError (error) {
      captureErrorAndNotify(error, 'Error updating asset type')
    }
  })

  function validate (values) {
    const errors = {}
    const {
      length,
      width,
      height,
      dimensionUom,
      maxWeight,
      weightUom,
      description,
      priceIncludes,
      price,
      rentalPeriod,
      overageFee,
      extensionFee
    } = values

    if (!length) {
      errors.length = 'Required'
    } else if (isNaN(length)) {
      errors.length = 'Must be a number'
    } else if (Number(length) < 0) {
      errors.length = 'Must be a positive number'
    }
    if (!width) {
      errors.width = 'Required'
    } else if (isNaN(width)) {
      errors.width = 'Must be a number'
    } else if (Number(width) < 0) {
      errors.width = 'Must be a positive number'
    }
    if (!height) {
      errors.height = 'Required'
    } else if (isNaN(height)) {
      errors.height = 'Must be a number'
    } else if (Number(height) < 0) {
      errors.height = 'Must be a positive number'
    }
    if (!dimensionUom) {
      errors.dimensionUom = 'Required'
    }
    if (!weightUom) {
      errors.weightUom = 'Required'
    }
    if (!description) {
      errors.description = 'Required'
    }
    if (!priceIncludes) {
      errors.priceIncludes = 'Required'
    }
    if (!maxWeight) {
      errors.maxWeight = 'Required'
    } else if (isNaN(maxWeight)) {
      errors.maxWeight = 'Must be a number'
    } else if (Number(maxWeight) < 0) {
      errors.maxWeight = 'Must be a positive number'
    }

    if (!rentalPeriod) {
      errors.rentalPeriod = 'Required'
    } else if (isNaN(rentalPeriod)) {
      errors.rentalPeriod = 'Must be a number'
    } else if (Number(rentalPeriod) < 0) {
      errors.rentalPeriod = 'Must be a positive number'
    }

    if (!pricingDisabled) {
      if (!price) {
        errors.price = 'Required'
      } else if (isNaN(price)) {
        errors.price = 'Must be a number'
      } else if (Number(price) < 0) {
        errors.price = 'Must be a positive number'
      }
      if (!overageFee) {
        errors.overageFee = 'Required'
      } else if (isNaN(overageFee)) {
        errors.overageFee = 'Must be a number'
      } else if (Number(overageFee) < 0) {
        errors.overageFee = 'Must be a positive number'
      }
      if (!extensionFee) {
        errors.extensionFee = 'Required'
      } else if (isNaN(extensionFee)) {
        errors.extensionFee = 'Must be a number'
      } else if (Number(extensionFee) < 0) {
        errors.extensionFee = 'Must be a positive number'
      }
    }

    return errors
  }

  function onClickHeader () {
    if (!allowExpand || !isEnabled) return
    setExpanded(!expanded)
  }

  function getCurrency (country) {
    if (country === 'CA') return 'CAD'

    return 'USD'
  }

  function moneyToCents (money) {
    if (money == null || money === '' || isNaN(money)) return null

    return Math.floor(money * 100)
  }

  function onSubmit (formValues) {
    updateResourceTypeMutation({
      assetTypeId: assetDetails.id,
      haulerId,
      onlineScheduleDescription: formValues.description,
      onlineSchedulePriceIncludesDetails: formValues.priceIncludes,
      onlineScheduleVisible: formValues.assetActive,
      defaultRentalPeriod: Number(formValues.rentalPeriod),
      onlineScheduleCostCents: moneyToCents(formValues.price),
      onlineScheduleCostCurrency: getCurrency(haulerCountry),
      onlineScheduleExtensionFeeCents: moneyToCents(formValues.extensionFee),
      onlineScheduleExtensionFeeCurrency: getCurrency(haulerCountry),
      dumpsterAttributes: {
        height: Number(formValues.height),
        width: Number(formValues.width),
        length: Number(formValues.length),
        dimensionUom: formValues.dimensionUom,
        maxWeight: Number(formValues.maxWeight),
        weightUom: formValues.weightUom,
        prohibitedMaterials: formValues.prohibitedMaterials ?? '',
        onlineScheduleOverageFeeCents: moneyToCents(formValues.overageFee),
        onlineScheduleOverageFeeCurrency: getCurrency(haulerCountry)
      }
    })
  }

  function dimensionsUOM () {
    if (haulerCountry !== 'US') {
      return {
        FEET: 'Feet',
        METERS: 'Meters'
      }
    }
    return {
      FEET: 'Feet'
    }
  }

  function weightUOM () {
    const uom = {}

    if (haulerCountry !== 'US') {
      uom.KILOGRAMS = 'Kilograms'
      uom.METRIC_TONS = 'Metric Tons'
    }

    uom.POUNDS = 'Pounds'
    uom.TONS = 'Tons'
    uom.CUBIC_YARDS = 'Cubic Yards'

    return uom
  }

  function buttonIcon () {
    if (expanded) return 'keyboard_double_arrow_up'
    return 'keyboard_double_arrow_down'
  }

  if (Object.keys(dimensionsUOM()).length === 1) {
    initialValues.dimensionUom = Object.keys(dimensionsUOM())[0]
  }

  return (
    <section className={cn('dis-panel', styles.sectionWrapper)}>
      <header className={cn('dis-panel-header')}>
        <button
          type='button'
          aria-label={`Expand ${assetDetails.name} to see product details`}
          disabled={!allowExpand || !isEnabled}
          onClick={onClickHeader}
          className={cn('dis-btn-blank', styles.headerBtn)}>
          <span className={cn(styles.headerGroup)}>
            {(allowExpand && isEnabled) && (
              <i className='material-icons'>{buttonIcon()}</i>
            )}
            <h3>
              <span
                className={cn('label label-pill ml-6', {
                  'label-success': isEnabled,
                  'label-danger': !isEnabled
                })}>
                {assetDetails.status.toUpperCase()}
              </span>
              <span className='pl-6'>
                {assetDetails.name} ({assetDetails.shortCode})
              </span>
            </h3>
          </span>
          <span className={cn(styles.headerGroup)}>
            <i
              className={cn('material-icons', {
                [styles.productNotVisible]: !assetDetails.onlineScheduleVisible
              })}
              title = {assetDetails.onlineScheduleVisible ? 'Visible on Online Schedule' : 'Not included on Online Schedule'}>
              {assetDetails.onlineScheduleVisible ? 'shopping_cart' : 'remove_shopping_cart'}
            </i>
          </span>
        </button>
      </header>
      <div className={cn('dis-panel-body', {
        [styles.panelHidden]: !expanded
      })}>
        <Form onSubmit={onSubmit} initialValues={initialValues} validate={validate}>
          {({ handleSubmit, pristine, values, valid }) => (
            <form onSubmit={handleSubmit}>
              <h4>
                Dimensions
              </h4>
              <p>Enter your dumpster details, including size and capacity, so customers can schedule and manage services accurately.</p>
              <div className={cn(styles.inlineFields)}>
                <Field name='length'>
                  {({ input, meta }) => (
                    <div className={cn('form-group', styles.textInput, {
                      'has-error': meta.error && meta.touched
                    })}>
                      <label className='control-label' htmlFor='length'>Length</label>
                      <input
                        {...input}
                        type='text'
                        className='form-control'
                        pattern='^-?\d*(\.\d+)?$'
                        maxLength={15}
                      />
                      {meta.error && meta.touched && <span>{meta.error}</span>}
                    </div>
                  )}
                </Field>
                <Field name='width'>
                  {({ input, meta }) => (
                    <div className={cn('form-group', styles.textInput, {
                      'has-error': meta.error && meta.touched
                    })}>
                      <label className='control-label' htmlFor='width'>Width</label>
                      <input
                        {...input}
                        type='text'
                        pattern='^-?\d*(\.\d+)?$'
                        className='form-control'
                        maxLength={15}
                      />
                      {meta.error && meta.touched && <span>{meta.error}</span>}
                    </div>
                  )}
                </Field>
                <Field name='height'>
                  {({ input, meta }) => (
                    <div className={cn('form-group', styles.textInput, {
                      'has-error': meta.error && meta.touched
                    })}>
                      <label className='control-label' htmlFor='height'>Height</label>
                      <input
                        {...input}
                        type='text'
                        pattern='^-?\d*(\.\d+)?$'
                        className='form-control'
                        maxLength={15}
                      />
                      {meta.error && meta.touched && <span>{meta.error}</span>}
                    </div>
                  )}
                </Field>
                <Field
                  name='dimensionUom'
                  label='Dimensions Unit of Measure'
                  component={SelectInput}
                  options={dimensionsUOM()}
                />
              </div>
              <div className={cn(styles.inlineFields)}>
                <Field name='maxWeight'>
                  {({ input, meta }) => (
                    <div className={cn('form-group', styles.textInput, {
                      'has-error': meta.error && meta.touched
                    })}>
                      <label className='control-label' htmlFor='maxWeight'>Max Weight Allowed</label>
                      <input
                        {...input}
                        type='text'
                        pattern='^-?\d*(\.\d+)?$'
                        className='form-control'
                        maxLength={15}
                      />
                      {meta.error && meta.touched && <span>{meta.error}</span>}
                    </div>
                  )}
                </Field>
                <Field
                  name='weightUom'
                  label='Weight Unit of Measure'
                  component={SelectInput}
                  options={weightUOM()}
                />
              </div>

              <h4>
                Product Details
              </h4>

              <Field
                component={TextareaInput}
                label='Add an asset description to help customers understand it, such as what materials are allowed, the types of jobs it works best for, or any other helpful notes.'
                name='description'
                placeholder=''
                maxLength={'1000'}
                textAreaClassName={cn(styles.largeTextArea)}
              />
              <Field
                component={TextareaInput}
                label='Add materials that cannot be put inside the dumpster or other restrictions that may apply. (Optional)'
                name='prohibitedMaterials'
                placeholder=''
                maxLength={'1000'}
                textAreaClassName={cn(styles.largeTextArea)}
              />
              <Field
                component={TextareaInput}
                label='Please include everything covered in the price, such as pickup, delivery, and taxes, so there are no surprises.'
                name='priceIncludes'
                placeholder=''
                maxLength={'1000'}
                textAreaClassName={cn(styles.largeTextArea)}
              />

              <h4>
                Prices and Fees
              </h4>
              <div className={cn(styles.inlineFields, {
                [styles.pricingFieldsDisabled]: pricingDisabled
              })}>
                <Field name='price'>
                  {({ input, meta }) => (
                    <div className={cn('form-group', styles.textInput, {
                      'has-error': meta.error && meta.touched
                    })}>
                      <label className='control-label' htmlFor='price'>Rental Price</label>
                      <input
                        {...input}
                        type='text'
                        pattern='^\d+(\.\d{2})?$'
                        className='form-control'
                        maxLength={15}
                        disabled={pricingDisabled}
                      />
                      {meta.error && meta.touched && <span>{meta.error}</span>}
                    </div>
                  )}
                </Field>
                <Field name='rentalPeriod'>
                  {({ input, meta }) => (
                    <div className={cn('form-group', styles.textInput, {
                      'has-error': meta.error && meta.touched
                    })}>
                      <label className='control-label' htmlFor='rentalPeriod'>Rental Period (in days)</label>
                      <input
                        {...input}
                        type='text'
                        pattern='^\d*(\.\d+)?$'
                        className='form-control'
                        maxLength={15}
                        disabled={pricingDisabled}
                      />
                      {meta.error && meta.touched && <span>{meta.error}</span>}
                    </div>
                  )}
                </Field>
                <Field name='overageFee'>
                  {({ input, meta }) => (
                    <div className={cn('form-group', styles.textInput, {
                      'has-error': meta.error && meta.touched
                    })}>
                      <label className='control-label' htmlFor='overageFee'>Weight Overage Fee (per above unit of measure)</label>
                      <input
                        {...input}
                        type='text'
                        pattern='^\d+(\.\d{2})?$'
                        className='form-control'
                        maxLength={15}
                        disabled={pricingDisabled}
                      />
                      {meta.error && meta.touched && <span>{meta.error}</span>}
                    </div>
                  )}
                </Field>
                <Field name='extensionFee'>
                  {({ input, meta }) => (
                    <div className={cn('form-group', styles.textInput, {
                      'has-error': meta.error && meta.touched
                    })}>
                      <label className='control-label' htmlFor='extensionFee'>Rental Extension Fee (per day)</label>
                      <input
                        {...input}
                        type='text'
                        pattern='^\d+(\.\d{2})?$'
                        className='form-control'
                        maxLength={15}
                        disabled={pricingDisabled}
                      />
                      {meta.error && meta.touched && <span>{meta.error}</span>}
                    </div>
                  )}
                </Field>
              </div>
              <div>
                <Field
                  type='checkbox'
                  disabled={!valid}
                  name='assetActive'
                  label='View on Online Schedule'
                  component={CheckboxInput}
                />
              </div>

              <div className={styles.buttonContainer}>
                <button
                  type='submit'
                  className='dis-btn dis-btn-primary dis-btn-lg'
                  disabled={pristine || !valid || isUpdating}>
                  <span>Save Changes</span>
                  <i className='material-icons'>check</i>
                </button>
              </div>
            </form>
          )}
        </Form>
      </div>
    </section>
  )
}

OnlineScheduleProductDetails.propTypes = {
  assetDetails: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    shortCode: PropTypes.string.isRequired,
    onlineScheduleVisible: PropTypes.bool,
    status: PropTypes.string.isRequired
  }).isRequired,
  startExpanded: PropTypes.bool,
  refetchAssetTypes: PropTypes.func,
  allowExpand: PropTypes.bool,
  haulerCountry: PropTypes.string,
  haulerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  pricingDisabled: PropTypes.bool,
  initialValues: PropTypes.shape({
    length: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    dimensionUom: PropTypes.oneOf(['FEET', 'METERS']),
    maxWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    weightUom: PropTypes.oneOf(['KILOGRAMS', 'METRIC_TONS', 'POUNDS', 'TONS', 'CUBIC_YARDS', 'GALLONS']),
    description: PropTypes.string,
    prohibitedMaterials: PropTypes.string,
    priceIncludes: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rentalPeriod: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    weightOverage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    extensionFee: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    assetActive: PropTypes.bool
  })
}
